import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PaginationParams {
  limit?: number;
  cursor?: string; // ISO timestamp for cursor pagination
  direction?: 'before' | 'after';
}

interface OptimizedCast {
  id: string;
  user_id: string;
  iso_request: string;
  location_address: string;
  broadcast_location_lat: number;
  broadcast_location_lng: number;
  location_radius_km: number;
  likes_count: number;
  comments_count: number;
  responses_count: number;
  solved: boolean;
  sentiment: string;
  created_at: string;
  // Joined profile data
  user: {
    name: string;
    username: string;
    avatar: string;
    verified: boolean;
  };
  // Computed fields
  isLiked?: boolean;
  timestamp: string;
}

export const useOptimizedCasts = (isLiteMode: boolean = false, enabled: boolean = true) => {
  const [casts, setCasts] = useState<OptimizedCast[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const defaultLimit = isLiteMode ? 3 : 7; // Fewer casts since they're less common

  // Only select columns that are actually rendered in CastCard
  const selectColumns = `
    id,
    user_id,
    iso_request,
    location_address,
    broadcast_location_lat,
    broadcast_location_lng,
    location_radius_km,
    likes_count,
    comments_count,
    responses_count,
    solved,
    sentiment,
    created_at
  `;

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return '1 day ago';
    return `${Math.floor(diffInHours / 24)} days ago`;
  };

  const fetchCasts = useCallback(async (
    pagination?: PaginationParams,
    skipLoadingState = false
  ) => {
    const limit = pagination?.limit || defaultLimit;
    
    try {
      if (!skipLoadingState) {
        setLoading(true);
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      
      // Build query
      let query = supabase
        .from('casts')
        .select(selectColumns)
        .order('created_at', { ascending: false })
        .limit(limit);

      // Add cursor pagination
      if (pagination?.cursor) {
        if (pagination.direction === 'after') {
          query = query.lt('created_at', pagination.cursor);
        } else {
          query = query.gt('created_at', pagination.cursor);
        }
      }

      const { data: castsData, error: castsError } = await query;

      if (castsError) {
        console.error('Database casts error:', castsError);
        setError('Failed to load casts from database');
        return;
      }

      if (!castsData || castsData.length === 0) {
        if (!pagination?.cursor) {
          setCasts([]);
        }
        setHasMore(false);
        return;
      }

      // Fetch profile data for all users in current batch
      const userIds = [...new Set(castsData.map(cast => cast.user_id))];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('user_id, username, farm_name, avatar_url')
        .in('user_id', userIds)
        .eq('is_active', true)
        .eq('is_discoverable', true);

      // Create profile lookup
      const profileLookup: { [key: string]: any } = {};
      if (profilesData) {
        profilesData.forEach(profile => {
          profileLookup[profile.user_id] = profile;
        });
      }

      // Get user likes for current batch
      let userLikes: string[] = [];
      if (user) {
        const { data: likesData } = await supabase
          .from('cast_likes')
          .select('cast_id')
          .eq('user_id', user.id)
          .in('cast_id', castsData.map(c => c.id));
        
        userLikes = likesData?.map(like => like.cast_id) || [];
      }

      // Transform casts with minimal data
      const transformedCasts: OptimizedCast[] = castsData.map((cast: any) => {
        const profile = profileLookup[cast.user_id];
        
        return {
          id: cast.id,
          user_id: cast.user_id,
          iso_request: cast.iso_request,
          location_address: cast.location_address,
          broadcast_location_lat: cast.broadcast_location_lat,
          broadcast_location_lng: cast.broadcast_location_lng,
          location_radius_km: cast.location_radius_km,
          likes_count: cast.likes_count || 0,
          comments_count: cast.comments_count || 0,
          responses_count: cast.responses_count || 0,
          solved: cast.solved || false,
          sentiment: cast.sentiment,
          created_at: cast.created_at,
          user: {
            name: profile?.farm_name || 'Unknown Farm',
            username: profile?.username || 'unknown',
            avatar: profile?.avatar_url || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
            verified: Math.random() > 0.5, // TODO: Add verified field to profiles
          },
          isLiked: userLikes.includes(cast.id),
          timestamp: formatTimestamp(cast.created_at),
        };
      });

      // Update state based on pagination
      if (pagination?.cursor && pagination.direction === 'after') {
        // Load more (append to existing)
        setCasts(prev => [...prev, ...transformedCasts]);
      } else {
        // Initial load or refresh
        setCasts(transformedCasts);
      }

      // Set pagination state
      setHasMore(transformedCasts.length === limit);
      setNextCursor(transformedCasts.length > 0 ? transformedCasts[transformedCasts.length - 1].created_at : null);

    } catch (err) {
      console.error('Error fetching casts:', err);
      setError('Failed to fetch casts');
      if (!pagination?.cursor) {
        setCasts([]);
      }
    } finally {
      setLoading(false);
    }
  }, [defaultLimit]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore && nextCursor) {
      fetchCasts({
        limit: defaultLimit,
        cursor: nextCursor,
        direction: 'after'
      });
    }
  }, [loading, hasMore, nextCursor, fetchCasts, defaultLimit]);

  const refresh = useCallback(() => {
    setNextCursor(null);
    setHasMore(true);
    fetchCasts();
  }, [fetchCasts]);

  const toggleLike = async (castId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Optimistic update for anonymous users
        setCasts(prevCasts => 
          prevCasts.map(cast => 
            cast.id === castId 
              ? { 
                  ...cast, 
                  isLiked: !cast.isLiked,
                  likes_count: cast.isLiked ? cast.likes_count - 1 : cast.likes_count + 1
                }
              : cast
          )
        );
        return;
      }

      // Check if user already liked this cast
      const { data: existingLike } = await supabase
        .from('cast_likes')
        .select('id')
        .eq('cast_id', castId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingLike) {
        // Remove like
        await supabase
          .from('cast_likes')
          .delete()
          .eq('cast_id', castId)
          .eq('user_id', user.id);
        
        // Update cast likes count
        const currentCast = casts.find(c => c.id === castId);
        const newCount = Math.max(0, (currentCast?.likes_count || 0) - 1);
        await supabase
          .from('casts')
          .update({ likes_count: newCount })
          .eq('id', castId);
      } else {
        // Add like
        await supabase
          .from('cast_likes')
          .insert({ cast_id: castId, user_id: user.id });
        
        // Update cast likes count
        const currentCast = casts.find(c => c.id === castId);
        const newCount = (currentCast?.likes_count || 0) + 1;
        await supabase
          .from('casts')
          .update({ likes_count: newCount })
          .eq('id', castId);
      }

      // Refresh casts to get updated counts
      refresh();
    } catch (err) {
      console.error('Error toggling cast like:', err);
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchCasts();
    } else {
      // Clear casts if fetching is disabled (Lite mode, different tab)
      setCasts([]);
      setLoading(false);
      setHasMore(false);
    }
  }, [fetchCasts, enabled]);

  return {
    casts,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    toggleLike,
    isLiteMode,
    retryCount: 0, // Add missing properties for consistency
    retry: refresh,
    isOffline: false
  };
};